import React, { useState } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { useAuth } from '../contexts/AuthContext'
import { db } from '../utils/firebase'
import { doc, setDoc, collection, addDoc, serverTimestamp } from 'firebase/firestore'
import { createUserWithEmailAndPassword } from 'firebase/auth'
import { auth } from '../utils/firebase'
import { Building2, User, Mail, Lock, MapPin, CheckCircle } from 'lucide-react'
import './AdminRegistrationPage.css'

function AdminRegistrationPage() {
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const sessionId = searchParams.get('session_id')
  const { loadUserProfile } = useAuth()
  
  const [step, setStep] = useState(1)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')
  
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    password: '',
    confirmPassword: '',
    neighborhoodName: '',
    role: 'admin' // 'admin' or 'boardMember'
  })

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    })
  }

  const handleSubmit = async (e) => {
    e.preventDefault()
    setError('')

    if (formData.password !== formData.confirmPassword) {
      setError('Passwords do not match')
      return
    }

    if (formData.password.length < 6) {
      setError('Password must be at least 6 characters')
      return
    }

    setLoading(true)

    try {
      // 1. Create Firebase Auth user
      const userCredential = await createUserWithEmailAndPassword(
        auth,
        formData.email,
        formData.password
      )
      const user = userCredential.user

      // 2. Create neighborhood document
      const neighborhoodRef = await addDoc(collection(db, 'neighborhoods'), {
        name: formData.neighborhoodName,
        createdAt: serverTimestamp(),
        createdBy: user.uid,
        subscription: {
          plan: 'monthly',
          priceId: 'price_1TGRanDKjuZ7t3KNCJhknoMu',
          currentPeriodStart: new Date(),
          currentPeriodEnd: new Date(Date.now() + 30 * 24 * 60 * 60 * 1000),
          status: 'trialing',
          planName: 'Free Trial',
          trialEndsAt: new Date(Date.now() + 30 * 24 * 60 * 60 * 1000) // 30 days from now
        }
      })

      // 3. Create user document
      await setDoc(doc(db, 'users', user.uid), {
        uid: user.uid,
        email: formData.email,
        name: `${formData.firstName} ${formData.lastName}`,
        firstName: formData.firstName,
        lastName: formData.lastName,
        neighborhoodId: neighborhoodRef.id,
        neighborhoodName: formData.neighborhoodName,
        isAdmin: formData.role === 'admin',
        isBoardMember: formData.role === 'boardMember',
        isSuperAdmin: false,
        createdAt: serverTimestamp(),
        stripeSessionId: sessionId || null
      })

      // 4. Re-fetch the user profile into AuthContext. The auth listener
      //    fired during createUserWithEmailAndPassword above, before the
      //    Firestore docs existed, so userProfile is currently null. Without
      //    this explicit reload the dashboard mounts and sits on "Loading
      //    dashboard…" forever, only resolving on a manual page refresh.
      await loadUserProfile(user.uid)

      // Success - redirect to dashboard
      navigate('/')
    } catch (err) {
      console.error('Registration error:', err)
      if (err.code === 'auth/email-already-in-use') {
        setError('An account with this email already exists')
      } else {
        setError(err.message || 'Failed to create account')
      }
    } finally {
      setLoading(false)
    }
  }

  return (
    <div className="admin-registration-page">
      <div className="registration-container">
        <div className="registration-header">
          <img src="/logo.jpeg" alt="NeighborTopia" className="logo" />
          <h1>Welcome to NeighborTopia!</h1>
          <p>Let's set up your neighborhood community</p>
        </div>

        {/* Progress Steps */}
        <div className="progress-steps">
          <div className={`step ${step >= 1 ? 'active' : ''}`}>
            <div className="step-number">1</div>
            <span>Your Info</span>
          </div>
          <div className="step-line"></div>
          <div className={`step ${step >= 2 ? 'active' : ''}`}>
            <div className="step-number">2</div>
            <span>Neighborhood</span>
          </div>
          <div className="step-line"></div>
          <div className={`step ${step >= 3 ? 'active' : ''}`}>
            <div className="step-number">3</div>
            <span>Your Role</span>
          </div>
        </div>

        <form onSubmit={handleSubmit}>
          {error && <div className="error-message">{error}</div>}

          {/* Step 1: Personal Info */}
          {step === 1 && (
            <div className="form-step">
              <h2>Create Your Account</h2>
              
              <div className="form-row">
                <div className="form-group">
                  <label>First Name</label>
                  <div className="input-with-icon">
                    <User size={18} />
                    <input
                      type="text"
                      name="firstName"
                      value={formData.firstName}
                      onChange={handleChange}
                      placeholder="First name"
                      required
                    />
                  </div>
                </div>
                <div className="form-group">
                  <label>Last Name</label>
                  <div className="input-with-icon">
                    <User size={18} />
                    <input
                      type="text"
                      name="lastName"
                      value={formData.lastName}
                      onChange={handleChange}
                      placeholder="Last name"
                      required
                    />
                  </div>
                </div>
              </div>

              <div className="form-group">
                <label>Email</label>
                <div className="input-with-icon">
                  <Mail size={18} />
                  <input
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    placeholder="you@example.com"
                    required
                  />
                </div>
              </div>

              <div className="form-group">
                <label>Password</label>
                <div className="input-with-icon">
                  <Lock size={18} />
                  <input
                    type="password"
                    name="password"
                    value={formData.password}
                    onChange={handleChange}
                    placeholder="At least 6 characters"
                    required
                  />
                </div>
              </div>

              <div className="form-group">
                <label>Confirm Password</label>
                <div className="input-with-icon">
                  <Lock size={18} />
                  <input
                    type="password"
                    name="confirmPassword"
                    value={formData.confirmPassword}
                    onChange={handleChange}
                    placeholder="Confirm your password"
                    required
                  />
                </div>
              </div>

              <button
                type="button"
                className="btn-primary btn-large"
                onClick={() => {
                  if (formData.firstName && formData.lastName && formData.email && formData.password && formData.confirmPassword) {
                    if (formData.password !== formData.confirmPassword) {
                      setError('Passwords do not match')
                    } else {
                      setError('')
                      setStep(2)
                    }
                  }
                }}
              >
                Continue
              </button>
            </div>
          )}

          {/* Step 2: Neighborhood Info */}
          {step === 2 && (
            <div className="form-step">
              <h2>What's your community's name?</h2>
              <p className="step-description">
                This is the name residents will see when they join your community.
              </p>

              <div className="form-group">
                <label>Neighborhood Name</label>
                <div className="input-with-icon">
                  <Building2 size={18} />
                  <input
                    type="text"
                    name="neighborhoodName"
                    value={formData.neighborhoodName}
                    onChange={handleChange}
                    placeholder="e.g., Maple Grove HOA, Sunset Hills Neighborhood Association"
                    required
                  />
                </div>
              </div>

              <div className="button-row">
                <button
                  type="button"
                  className="btn-outline"
                  onClick={() => setStep(1)}
                >
                  Back
                </button>
                <button
                  type="button"
                  className="btn-primary"
                  onClick={() => {
                    if (formData.neighborhoodName) {
                      setStep(3)
                    }
                  }}
                >
                  Continue
                </button>
              </div>
            </div>
          )}

          {/* Step 3: Role Selection */}
          {step === 3 && (
            <div className="form-step">
              <h2>Select Your Role</h2>
              <p className="step-description">
                Both roles have the same permissions. Choose the title that best fits your position.
              </p>

              <div className="role-options">
                <label className={`role-option ${formData.role === 'admin' ? 'selected' : ''}`}>
                  <input
                    type="radio"
                    name="role"
                    value="admin"
                    checked={formData.role === 'admin'}
                    onChange={handleChange}
                  />
                  <div className="role-content">
                    <div className="role-icon admin">
                      <User size={24} />
                    </div>
                    <div className="role-info">
                      <h3>Neighborhood Admin</h3>
                      <p>Property manager, community manager, or HOA / neighborhood association administrator</p>
                    </div>
                    {formData.role === 'admin' && <CheckCircle className="check-icon" size={24} />}
                  </div>
                </label>

                <label className={`role-option ${formData.role === 'boardMember' ? 'selected' : ''}`}>
                  <input
                    type="radio"
                    name="role"
                    value="boardMember"
                    checked={formData.role === 'boardMember'}
                    onChange={handleChange}
                  />
                  <div className="role-content">
                    <div className="role-icon board">
                      <Building2 size={24} />
                    </div>
                    <div className="role-info">
                      <h3>Board Member</h3>
                      <p>HOA or neighborhood association board president, treasurer, secretary, or director</p>
                    </div>
                    {formData.role === 'boardMember' && <CheckCircle className="check-icon" size={24} />}
                  </div>
                </label>
              </div>

              <p className="role-note">
                <strong>Note:</strong> Both roles have equal permissions. You can manage the community, 
                create announcements, moderate discussions, and invite residents.
              </p>

              <div className="button-row">
                <button
                  type="button"
                  className="btn-outline"
                  onClick={() => setStep(2)}
                >
                  Back
                </button>
                <button
                  type="submit"
                  className="btn-primary"
                  disabled={loading}
                >
                  {loading ? 'Creating...' : 'Create My Community'}
                </button>
              </div>
            </div>
          )}
        </form>
      </div>
    </div>
  )
}

export default AdminRegistrationPage
